<template>
  <div class="container container-padding">
    <LoaderComponent v-if="loading" />
    <div v-else>
      <h2>Översättningar</h2>
      <p>
        Översättningarna är uppdelade på ett par olika grupper i en mall. Mallen innehåller tillgängliga strängar på engelska som sen kan översättas för var
        språk.
      </p>
      <p>När du lägger till en ny sträng i mallen blir det tillgängligt för samtliga språk.</p>

      <p>Översättningar ska läggas in som gemener, med undantag för akronymer och inledande bokstav i översättning med flera meningar</p>

      <div class="language-select" :style="store.topMenuStyle">
        <ToggleComponent v-model="currentLanguage" :options="availableLanguages" />
        <input v-model.trim="searchTerm" type="search" placeholder="Sök sträng..." class="form-control mt-2 mb-2" />
      </div>

      <small v-if="compareTerm">Visar filtrerat resultat</small>
      <div v-for="(strings, translationNamespace) in displayedTranslations" :key="translationNamespace">
        <AccordionComponent
          :title="`${translate(toTitle(translationNamespace), ['taxonomies', 'application'], 'capitalize')} (${getFilteredStrings(strings, true)})`"
          :disabled="Boolean(!getFilteredStrings(strings, true))"
          :start-collapsed="!expandedAccordions.has(String(translationNamespace))"
          style="top: 6em"
          @state="setExpandedAccordion(String(translationNamespace), $event.value)"
        >
          <table class="w-100">
            <thead>
              <tr class="header mb-2">
                <th />
                <th class="w-50">Engelsk sträng</th>
                <th class="w-50">Översättning</th>
                <th />
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(translation, string) in getFilteredStrings(strings)"
                :key="translation"
                class="mb-2"
                :class="{
                  'was-moved': wasMoved(string, String(translationNamespace)),
                  'has-changes': changedTranslations[translationNamespace]?.[string],
                  deleted: wasDeleted(changedTranslations[translationNamespace]?.[string]),
                }"
              >
                <td>
                  <button
                    class="link"
                    :disabled="wasDeleted(changedTranslations[translationNamespace]?.[string])"
                    title="Editera sträng"
                    @click="setEditString(String(translationNamespace), string)"
                  >
                    <i class="fa fa-edit" />
                  </button>
                </td>
                <td>{{ string }}</td>
                <td>
                  <input
                    type="text"
                    class="w-100"
                    :disabled="wasDeleted(changedTranslations[translationNamespace]?.[string])"
                    :value="
                      changedTranslations[translationNamespace]?.[string]?.[currentLanguage] !== undefined
                        ? changedTranslations[translationNamespace]?.[string]?.[currentLanguage]
                        : translation?.[currentLanguage]
                    "
                    @input="
                      setEnsuredInnerValue(changedTranslations, [translationNamespace, string, currentLanguage], ($event.target as HTMLInputElement).value)
                    "
                  />
                </td>
                <td style="white-space: nowrap">
                  <button
                    class="link ml-2"
                    :class="{
                      invisible: wasDeleted(changedTranslations[translationNamespace]?.[string]),
                    }"
                    title="Markera sträng som 'ta bort' (påverkar samtliga språk)"
                    @click="setDeleteString(String(translationNamespace), string)"
                  >
                    <i class="fa fa-trash" />
                  </button>
                  <button
                    class="link ml-2"
                    :class="{
                      invisible: wasMoved(string, String(translationNamespace)) || !changedTranslations[translationNamespace]?.[string],
                    }"
                    title="Återställ"
                    @click="resetTranslation(String(translationNamespace), string)"
                  >
                    <i class="fa fa-undo" />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </AccordionComponent>
      </div>

      <!--------------------->
      <!-- Add/Edit string -->
      <!--------------------->
      <ModalComponent
        v-if="stagedNewString"
        :activated="true"
        :block-quick-close="!isPristine"
        @close=";(stagedNewString = undefined), (stagedEditedString = undefined)"
      >
        <template #header>
          <h3>
            {{ stagedEditedString ? `Redigera sträng '${stagedEditedString.name}'` : 'Ny sträng' }}
          </h3>
        </template>
        <form @submit.prevent="stagedEditedString ? editString() : addNewString()">
          <div class="form-group mt-2">
            <label class="required d-block">
              Sträng på engelska
              <input id="name" v-model="stagedNewString.name" type="text" class="form-control" autocomplete="off" />
            </label>
          </div>

          <div class="form-group">
            <label class="required">
              Grupp
              <select v-model="stagedNewString.translationNamespace" class="form-control" @input="lastUsedGroup = ($event.target as HTMLInputElement).value">
                <option v-for="group of availableTranslationNamespaces" :key="group.value" :value="group.value">
                  {{ group.displayValue }}
                </option>
              </select>
            </label>
          </div>

          <div class="form-group">
            Översättningar
            <div class="row">
              <div v-for="language of store.availableLanguages" :key="language" class="col-md-6">
                <label class="w-100">
                  {{ translate(language, 'languages', undefined, store.translationLanguage) }}
                  <input v-model="stagedNewString.translations[language as Language]" type="text" class="form-control mb-1" />
                </label>
              </div>
            </div>
          </div>

          <hr />
          <p class="small-errormsg">
            {{ errorMsg }}
          </p>

          <button class="btn btn-primary mr-2" type="submit" :disabled="!stagedNewString.name?.trim() || !stagedNewString.translationNamespace || isPristine">
            Ok
          </button>
          <button class="btn btn-warning" type="reset" @click=";(stagedNewString = undefined), (stagedEditedString = undefined)">Avbryt</button>
        </form>
      </ModalComponent>

      <!-------------------->
      <!-- Action buttons -->
      <!-------------------->
      <div class="sticky-action-buttons d-flex">
        <template v-if="hasPendingChanges">
          <ActionPendingButton class="mr-2" :pending="actionPending" @click="saveTranslations()">Spara ändringar</ActionPendingButton>
          <button class="btn btn-warning mr-2" @click="resetTranslations(true)">Återställ ändringar</button>
        </template>
        <button class="btn btn-outline-primary" @click.stop="setCreateString()">Lägg till ny sträng</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import AccordionComponent from '@/components/AccordionComponent.vue'
import ActionPendingButton from '@/components/ActionPendingButton.vue'
import ModalComponent from '@/components/ModalComponent.vue'
import ToggleComponent from '@/components/ToggleComponent.vue'
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import { useTranslation } from '@/composables/useTranslation'
import { AnyObject } from '@/models/GenericInterfaces'
import { Language, Option } from '@/models/Global'
import Message from '@/models/Message'
import { StagedTranslation, StagedTranslationDocument, TranslationDocument } from '@/models/Translation'
import DataService from '@/services/DataService'
import { useDefaultStore } from '@/store'
import { camelCaseToTerm, clone, mergeObjects, objectsAreEqual, removeEmptyObjects, setEnsuredInnerValue, toTitle } from '@/utils/tools'
import { deleteField } from 'firebase/firestore'
import { computed, onMounted, ref, watch } from 'vue'

// -----------
// -- Setup --
// -----------

defineOptions({ name: 'TranslationsView' })

const expandedAccordions = new Set<string>() // non reactive variable
const { translate } = useTranslation()
const store = useDefaultStore()

// --------------------
// -- Reactives vars --
// --------------------

const currentLanguage = ref(store.translationLanguage)
const displayedTranslations = ref<StagedTranslationDocument>({})
const changedTranslations = ref<StagedTranslationDocument>({}) // the object firebase writes to the database (merges with existing)
const loading = ref(false)
const stagedNewString = ref<{
  translationNamespace?: string
  name: string
  translations: Partial<Record<Language, string>>
}>()

const stagedEditedString = ref<{
  translationNamespace: string
  name: string
  translations: Partial<Record<Language, string>>
}>()
const searchTerm = ref('')
const actionPending = ref(false)
const errorMsg = ref('')
const lastUsedGroup = ref<string>()
const movedTranslations = ref<
  Array<{
    name: string
    fromTranslationNamespace: string
    toTranslationNamespace: string
  }>
>([])

// --------------------
// -- Computed props --
// --------------------

const compareTerm = computed(() => searchTerm.value.toLowerCase())

const availableTranslationNamespaces = computed((): Option[] => {
  return Object.keys(displayedTranslations.value || {}).map((namespace) => ({
    displayValue: translate(camelCaseToTerm(namespace), ['taxonomies', 'application'], 'capitalize', store.translationLanguage),
    value: namespace,
  }))
})

const hasPendingChanges = computed(() => Object.keys(changedTranslations.value || {}).length)
const availableLanguages = computed((): Option[] =>
  store.availableLanguages.map((language: Language) => ({
    value: language,
    displayValue: translate(language, 'languages', 'lowercase'),
  })),
)

const isPristine = computed(() => {
  return stagedEditedString.value
    ? stagedEditedString.value?.name === stagedNewString.value?.name &&
        stagedEditedString.value?.translationNamespace === stagedNewString.value?.translationNamespace &&
        objectsAreEqual(stagedNewString.value?.translations || {}, stagedEditedString.value?.translations || {})
    : !stagedNewString.value?.name && !Object.values(stagedNewString.value?.translations || {}).filter((t) => !!t).length
})

// ---------------------
// -- Lifecycle hooks --
// ---------------------

onMounted(async () => {
  watch([() => store.translations, () => store.translationLanguage], () => {
    if (hasPendingChanges.value) {
      displayedTranslations.value = mergeObjects(false, displayedTranslations.value, getSortedTranslations(store.translations))
    } else {
      displayedTranslations.value = getSortedTranslations(store.translations)
    }
  })

  loading.value = true
  await resetTranslations()
  loading.value = false
})

// -------------
// -- Methods --
// -------------

function getSortedTranslations(translations: TranslationDocument | undefined) {
  return Object.entries(translations || {})
    .sort(([a], [b]) => translate(toTitle(a), ['taxonomies', 'application']).localeCompare(translate(toTitle(b), ['taxonomies', 'application'])))
    .reduce((acc, [key, value]) => {
      const sortedValues = Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .reduce(
          (acc, [k, v]) => ({
            ...acc,
            [k]: v,
          }),
          {},
        )
      acc[key] = sortedValues
      return acc
    }, {} as AnyObject)
}

async function resetTranslations(confirmReset?: boolean) {
  if (!confirmReset || confirm(`Vill du återställa dina ändringar?`)) {
    const translations = clone(store.translations || {})
    displayedTranslations.value = getSortedTranslations(translations)
    changedTranslations.value = {}
    movedTranslations.value = []
  }
}

async function resetTranslation(translationNamespace: string, string: string) {
  const index = movedTranslations.value.findIndex((translation) => translation.name === string && translation.toTranslationNamespace === translationNamespace)
  const movedRecord = movedTranslations.value[index]
  if (index >= 0) {
    movedTranslations.value.splice(index, 1)
  }

  // reset displayed translations
  const liveTranslation = store.translations?.[movedRecord?.fromTranslationNamespace || translationNamespace]?.[string]

  delete displayedTranslations.value[translationNamespace][string]
  if (liveTranslation) {
    displayedTranslations.value[movedRecord?.fromTranslationNamespace || translationNamespace][string] = liveTranslation
  } else if (movedRecord?.fromTranslationNamespace) {
    delete displayedTranslations.value[movedRecord.fromTranslationNamespace][string]
  }

  delete changedTranslations.value[translationNamespace]?.[string]
  delete changedTranslations.value[movedRecord?.fromTranslationNamespace]?.[string]
}

function setCreateString() {
  errorMsg.value = ''
  stagedNewString.value = {
    name: '',
    translationNamespace: lastUsedGroup.value,
    translations: {},
  }
}

function setEditString(translationNamespace: string, string: string) {
  errorMsg.value = ''
  stagedEditedString.value = {
    translationNamespace,
    name: string,
    translations: (changedTranslations.value[translationNamespace]?.[string] || displayedTranslations.value[translationNamespace][string]) as StagedTranslation,
  }
  stagedNewString.value = clone(stagedEditedString.value)
}

async function setDeleteString(translationNamespace: string, string: string) {
  setEnsuredInnerValue(changedTranslations.value, [translationNamespace, string], deleteField())
}

async function addNewString() {
  const string = stagedNewString.value?.name.trim().toLowerCase()
  if (stagedNewString.value?.translationNamespace && string) {
    if (!displayedTranslations.value[stagedNewString.value.translationNamespace]?.[string]) {
      const stringTranslations = (store.availableLanguages as Language[]).reduce(
        (acc: Partial<Record<Language, string>>, language) => ({
          ...acc,
          [language]: '',
        }),
        {},
      )
      setEnsuredInnerValue(displayedTranslations.value, [stagedNewString.value.translationNamespace, string], stringTranslations)
      setEnsuredInnerValue(changedTranslations.value, [stagedNewString.value.translationNamespace, string], stagedNewString.value.translations)

      stagedNewString.value = undefined
    } else {
      errorMsg.value = `Strängen '${string}' finns redan i gruppen`
    }
  }
}

function editString() {
  if (stagedEditedString.value?.translationNamespace && stagedNewString.value?.translationNamespace) {
    const newString = stagedNewString.value.name.trim().toLowerCase()

    const moveRecord = movedTranslations.value.find(
      (translation) =>
        translation.name === stagedEditedString.value?.name && translation.toTranslationNamespace === stagedEditedString.value?.translationNamespace,
    )

    if (
      stagedNewString.value.translationNamespace !== stagedEditedString.value.translationNamespace &&
      displayedTranslations.value[stagedNewString.value.translationNamespace][newString] &&
      !moveRecord
    ) {
      errorMsg.value = `Strängen '${newString}' är upptagen. Gruppen du vill flytta strängen till har redan en sträng med samma namn`
    } else {
      // add a moved record if the edited string is moved
      if (stagedEditedString.value?.translationNamespace !== stagedNewString.value?.translationNamespace) {
        if (moveRecord) {
          moveRecord.toTranslationNamespace = stagedNewString.value.translationNamespace

          delete displayedTranslations.value[stagedEditedString.value.translationNamespace]?.[stagedEditedString.value.name]

          delete changedTranslations.value[stagedEditedString.value.translationNamespace]?.[stagedEditedString.value.name]
        } else {
          movedTranslations.value.push({
            name: stagedEditedString.value.name,
            fromTranslationNamespace: stagedEditedString.value.translationNamespace,

            toTranslationNamespace: stagedNewString.value.translationNamespace,
          })
          setEnsuredInnerValue(changedTranslations.value, [stagedEditedString.value.translationNamespace, stagedEditedString.value.name], deleteField())
        }
        const translations = displayedTranslations.value?.[stagedEditedString.value.translationNamespace][stagedEditedString.value.name]

        setEnsuredInnerValue(displayedTranslations.value, [stagedNewString.value.translationNamespace, newString], translations)
      }

      setEnsuredInnerValue(changedTranslations.value, [stagedNewString.value.translationNamespace, newString], stagedNewString.value.translations)

      stagedEditedString.value = undefined
      stagedNewString.value = undefined
    }
  }
}

async function saveTranslations() {
  actionPending.value = true
  removeEmptyObjects(changedTranslations.value, 1) // !important. remove empty groups (depth 1) to prevent overwriting
  await DataService.saveTranslations(changedTranslations.value)
  store.addMessage(new Message(`Översättningarna har sparats`, undefined, 'success', 3))
  actionPending.value = false
  resetTranslations()
}

function getFilteredStrings(strings: StagedTranslation, onlyCount?: boolean) {
  const isIncluded = ([string, translation]: any[]) =>
    string.includes(compareTerm.value) || translation?.[currentLanguage.value]?.toLowerCase().includes(compareTerm.value)

  return onlyCount
    ? Object.entries(strings).reduce((sum, [string, translation]) => (sum += isIncluded([string, translation]) ? 1 : 0), 0)
    : Object.entries(strings)
        .filter(isIncluded)
        .reduce(
          (acc, [string, translation]) => ({
            ...acc,
            [string]: translation,
          }),
          {},
        )
}

function setExpandedAccordion(translationNamespace: string, state: boolean) {
  if (state) {
    expandedAccordions.add(translationNamespace)
  } else {
    expandedAccordions.delete(translationNamespace)
  }

  if (!lastUsedGroup.value) {
    lastUsedGroup.value = translationNamespace
  }
}

function wasDeleted(obj: AnyObject) {
  return obj?._methodName === 'deleteField'
}

function wasMoved(string: string, fromTranslationNamespace: string) {
  return movedTranslations.value.find(
    (translation) =>
      translation.name === string &&
      translation.fromTranslationNamespace === fromTranslationNamespace &&
      translation.toTranslationNamespace !== fromTranslationNamespace,
  )
}
</script>

<style lang="scss" scoped>
.language-select {
  position: sticky;
  padding: 5px 0.5em 0;
  z-index: 2;
  background-color: #fff;
  margin: 0 0.1em;
}

.card-body tr {
  &:not(.sticky-header):nth-child(2n) {
    background-color: #f5f5f5;
  }

  button {
    outline: none;
  }

  &.has-changes {
    background-color: #0906 !important;
  }

  &.deleted {
    background-color: #9006 !important;

    td,
    input {
      text-decoration: line-through;
    }
  }

  &.was-moved {
    background-color: #ccc !important;
  }

  &.header {
    z-index: 1;

    // position: sticky;
    // height: 2em;
    // top: 93px;

    padding: 0.5em 0;
    margin: 0 -1em;

    font-weight: bold;
    background-color: #fff;
  }
}
</style>
